import { useEffect, useState } from "react";
import { MessageCircle, X, Phone, Mail, MapPin, Facebook } from "lucide-react";
import { toast } from "sonner";

const DEFAULTS = {
  whatsappPhone: "+27678359550",
  facebookUrl: "https://www.facebook.com/profile.php?id=100083594971203",
  supportEmail: "qwa2talentshow@gmail.com",
  supportPhone: "+27 67 835 9550",
  address: "1105 Bophelong Village, Phuthaditjhaba, Free State, South Africa",
};

export default function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [bottom, setBottom] = useState(20);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const h = window.innerHeight;
      const doc = document.documentElement.scrollHeight;
      // Lift the button when approaching the bottom of the page
      setBottom(y + h >= doc - 50 ? 90 : 20);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openWhatsApp = () => {
    const num = DEFAULTS.whatsappPhone.replace(/[^\d]/g, "");
    const msg = `Hi! I'd like to know more about QwaQwa Talent Show events.\n${window.location.href}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const openFacebook = () => {
    if (!DEFAULTS.facebookUrl || DEFAULTS.facebookUrl === "#") {
      toast.info("Coming Soon!", { description: "Facebook page link is currently being updated." });
    } else {
      window.open(DEFAULTS.facebookUrl, "_blank");
    }
  };

  return (
    <>
{!open && (
  <button
    onClick={() => setOpen(true)}
    style={{
      bottom: `${bottom}px`,
      transition: 'bottom 0.3s ease-in-out',
    }}
    className="fixed right-5 z-50 group"
    aria-label="Open support chat"
  >

    {/* Glow */}
    <div className="absolute inset-0 rounded-full bg-QWA2_TALENT_SHOW-gold/30 blur-xl opacity-70 group-hover:opacity-100 transition duration-300" />

    {/* Pulse Ring */}
    <div className="absolute inset-0 rounded-full border border-QWA2_TALENT_SHOW-gold/40 animate-ping" />

    {/* Main Button */}
    <div className="relative w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-QWA2_TALENT_SHOW-red via-[#ff5f5f] to-QWA2_TALENT_SHOW-gold border border-white/20 shadow-[0_20px_50px_rgba(255,120,0,0.35)] flex items-center justify-center transition-all duration-300 group-hover:scale-105 active:scale-95">

      {/* Shine */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-700">
        <div className="absolute -left-10 top-0 h-full w-10 bg-white/30 skew-x-12 blur-lg group-hover:left-[140%] transition-all duration-1000" />
      </div>

      {/* Icon */}
      <MessageCircle
        size={30}
        className="relative z-10 text-white transition-transform duration-300 group-hover:scale-110"
      />

    </div>

  </button>
)}

      {open && (
        <div
          style={{ bottom: `${bottom}px`, transition: 'bottom 0.3s ease-in-out' }}
          className="fixed right-5 z-50 w-[20rem] md:w-[22rem] rounded-[2.5rem] shadow-2xl bg-white border border-QWA2_TALENT_SHOW-gold/20 overflow-hidden transition-all duration-300 animate-in fade-in slide-in-from-bottom-4"
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white">
            <div className="shrink-0 grid place-items-center w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30">
              <MessageCircle size={20} />
            </div>
            <div className="text-sm">
              <div className="font-black uppercase tracking-[0.1em]">Qwa2 Support</div>
              <div className="text-[10px] opacity-80 font-bold uppercase tracking-widest leading-none mt-0.5">Always here to help</div>
            </div>
            <button
              className="ml-auto rounded-full p-2 hover:bg-white/10 transition-colors"
              aria-label="Close help"
              onClick={() => setOpen(false)}
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Quick buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={openWhatsApp}
                className="rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/10 px-3 py-3 text-sm font-black text-QWA2_TALENT_SHOW-black hover:bg-QWA2_TALENT_SHOW-gold hover:text-white hover:border-QWA2_TALENT_SHOW-gold transition-all flex items-center justify-center gap-2"
              >
                <MessageCircle size={16} />
                Chat
              </button>
              <button
                onClick={openFacebook}
                className="rounded-2xl border-2 border-QWA2_TALENT_SHOW-red/10 px-3 py-3 text-sm font-black text-QWA2_TALENT_SHOW-black hover:bg-QWA2_TALENT_SHOW-red hover:text-white hover:border-QWA2_TALENT_SHOW-red transition-all flex items-center justify-center gap-2"
              >
                <Facebook size={16} />
                Follow
              </button>
            </div>

            {/* Contacts */}
            <div className="rounded-[1.5rem] bg-gray-50 border border-gray-100 p-5 space-y-4">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Get in Touch</div>
              <a href={`tel:${DEFAULTS.supportPhone}`} className="flex items-center gap-3 text-sm font-bold text-gray-700 hover:text-QWA2_TALENT_SHOW-red transition-colors">
                <Phone size={16} className="text-QWA2_TALENT_SHOW-red" /> {DEFAULTS.supportPhone}
              </a>
              <a href={`mailto:${DEFAULTS.supportEmail}`} className="flex items-center gap-3 text-sm font-bold text-gray-700 hover:text-QWA2_TALENT_SHOW-gold transition-colors break-all">
                <Mail size={16} className="text-QWA2_TALENT_SHOW-gold" /> {DEFAULTS.supportEmail}
              </a>
              {DEFAULTS.address && (
                <div className="flex items-start gap-3 text-[11px] text-gray-500 leading-relaxed font-bold uppercase tracking-wider pt-3 border-t border-gray-200/60">
                  <MapPin size={16} className="shrink-0 text-gray-400" />
                  <span className="line-clamp-2">{DEFAULTS.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
